import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LoadingSpinner } from '@paypalcorp/pp-react';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { Helmet } from 'react-helmet-async';
import styles from './Preview.module.less';
import IntegrationPageSectionContext from '../StateManagement/IntegrationPageSectionContext';
import { trackButtonClick } from '../../../lib/fpti';
import { SellerCardsWrapper } from './SellerCardsWrapper';
import { PARTNER } from '../../../lib/constants';
import isMobile from '../../../util/isMobile';

import {
  createOrder,
  captureOrder,
  authorizeOrder,
  shippingAddressChange,
  shippingOptionsChange,
  captureAuthorize,
  setupTokens,
  createPaymentToken,
  shippingServerSideCallback
} from '../../../util/sdkCallbacks';
import TestPanelContext from '../StateManagement/TestPanelContext';
import { isUserLoggedInSelector } from '../../../store/App/selectors';
import { useParams } from 'react-router';
import usePolling from '../../../util/usePolling';
import formatOneTimeCharges from '../../../util/formatOneTimeCharges';

const PreviewWrapper = () => {
  const [buttonLoading, setButtonLoading] = useState(true);
  const isUserLoggedIn = useSelector(isUserLoggedInSelector);
  const appIntegrationType = useSelector((state) => state?.app?.appIntegrationType);
  const {
    ButtonConfigurator: { buttonSettings = {} } = {},
    SdkConfigurator: { sdkSettings },
    OrderConfigurator: {
      amount: { value: orderAmount } = {},
      sellerId,
      buyerEmailAddress,
      contactPreference,
      contactData,
      updateSellerId
    } = {},
    VaultSetupTokenConfigurator: {
      selectedUsagePattern,
      price,
      selectedFrequency,
      intervalCount,
      totalCycles,
      startDate,
      shipping,
      oneTimeCharges,
      name,
      product,
      addPaymentToken
    },
    apps,
    code: {
      status: {
        handleShippingAddress = false,
        configureButtonMessages = false,
        serverSideShippingCallback = false
      } = {}
    } = {},
    lastCapture: { updateLastCapture },
    appSwitch
  } = useContext(IntegrationPageSectionContext);

  const vaultTokenOptions = {
    selectedUsagePattern,
    price,
    selectedFrequency,
    intervalCount,
    totalCycles,
    startDate,
    shipping,
    oneTimeCharges: formatOneTimeCharges(oneTimeCharges, sdkSettings?.currency),
    name,
    product
  };

  /* 
  Maintaining a Ref here to allow access to variables inside the  functions passed into the SDK. These functions are probably being memoized and
  have closure that is locked once the SDK init is called. Hence, even for values that the SDK is not directly dependent on, a Re-render was required
  to update the closure of above functions. The Ref solves this problem as it makes the latest value available.
  
  Note: Update the value in the Ref from a useEffect whenever the original value changes.
  */

  const nonRerenderValues = useRef({});

  const { updateLogs, toggleTestPanel } = useContext(TestPanelContext);
  const { subPage } = useParams();

  const { clientId } = sdkSettings;

  const {
    data: pollingData,
    startPolling,
    stopPolling
  } = usePolling('/studio/api/v1/callback/:orderId', isUserLoggedIn ? clientId : null, 5000);

  const csrfToken = useSelector((state) => state?.app?.csrfToken);

  useEffect(() => {
    nonRerenderValues.current.contactPreference = contactPreference;
  }, [contactPreference]);

  useEffect(() => {
    nonRerenderValues.current.contactData = contactData;
  }, [contactData]);

  const handleCreateOrder = async () => {
    try {
      const orderId = await createOrder({
        currencyCode: sdkSettings?.currency,
        amount: orderAmount,
        intent: sdkSettings?.intent,
        enableShippingOptions: handleShippingAddress,
        enableServerSideShippingCallback: serverSideShippingCallback,
        csrfToken,
        updateLogs,
        clientId: isUserLoggedIn ? clientId : null,
        appIntegrationType,
        sellerId,
        appSwitch,
        contactPreference: nonRerenderValues.current.contactPreference,
        contactData: nonRerenderValues.current.contactData,
        buyerEmailAddress
      });
      if (serverSideShippingCallback) {
        startPolling({ orderId });
      }
      toggleTestPanel(true);
      return orderId;
    } catch {
      return null;
    }
  };

  const handlePaymentToken = async ({ vaultSetupToken }) => {
    await createPaymentToken({
      vaultSetupToken,
      csrfToken,
      updateLogs,
      clientId: isUserLoggedIn ? clientId : null,
      addPaymentToken
    });
  };

  const handleCaptureOrder = async ({ orderID }) => {
    await captureOrder({
      orderId: orderID,
      csrfToken,
      updateLogs,
      updateLastCapture,
      clientId: isUserLoggedIn ? clientId : null,
      appIntegrationType,
      sellerId
    });
    toggleTestPanel(true);
  };

  const handleAuthorizeOrder = async ({ orderID }) => {
    const authorizationId = await authorizeOrder({
      orderId: orderID,
      clientId: isUserLoggedIn ? clientId : null,
      csrfToken,
      updateLogs,
      appIntegrationType,
      sellerId
    });
    await captureAuthorize({
      authorizationId,
      csrfToken,
      updateLogs,
      updateLastCapture,
      clientId: isUserLoggedIn ? clientId : null,
      appIntegrationType,
      sellerId
    });
    toggleTestPanel(true);
  };

  const onApprove = (data) => {
    stopPolling();
    if (subPage === 'recurring') {
      handlePaymentToken(data);
    } else {
      if (sdkSettings.intent === 'capture') {
        handleCaptureOrder(data);
      } else {
        handleAuthorizeOrder(data);
      }
    }
  };

  const handleShippingAddressChange = (data) => {
    if (handleShippingAddress) {
      shippingAddressChange(data, updateLogs);
    }
  };

  const handleShippingOptionsChange = (data) => {
    if (handleShippingAddress) {
      shippingOptionsChange(data, updateLogs);
    }
  };

  const createVaultSetupToken = async () => {
    const tokenId = await setupTokens({
      clientId: isUserLoggedIn ? clientId : null,
      csrfToken,
      updateLogs,
      selectedUsagePattern,
      vaultTokenOptions
    });
    toggleTestPanel(true);
    return tokenId;
  };

  useEffect(() => {
    if (pollingData?.request) {
      shippingServerSideCallback(pollingData, updateLogs);
    }
  }, [pollingData]);

  /* 
    Render PayPal Buttons using the JS SDK if App Switch is Enabled ... 
    We will revert to using the React SDK once App Switch is configured within the PayPal Buttons Wrapper.
  */
  const paypalButtonContainer = useRef(null);
  const paypalButton = useRef(null);

  useEffect(() => {
    setButtonLoading(true);
    if (appSwitch && isMobile()) {
      const script = document.createElement('script');
      script.async = true;
      script.onload = () => {
        setButtonLoading(false);
        paypalButton.current = window.paypal.Buttons({
          style: buttonSettings,
          appSwitchWhenAvailable: true,
          fundingSource: '',
          onInit: () => setButtonLoading(false),
          createVaultSetupToken: subPage === 'recurring' ? createVaultSetupToken : null,
          createOrder: handleCreateOrder,
          onApprove: onApprove,
          onClick: (data) => trackButtonClick(data.fundingSource),
          onCancel: stopPolling,
          onError: stopPolling,
          ...(handleShippingAddress
            ? {
                onShippingAddressChange: (data) => handleShippingAddressChange(data),
                onShippingOptionsChange: (data) => handleShippingOptionsChange(data)
              }
            : {}),
          ...(configureButtonMessages ? { message: { amount: Number(orderAmount) } } : {})
        });
        if (paypalButton.current.hasReturned()) {
          paypalButton.current.resume();
        } else {
          paypalButton.current.render(paypalButtonContainer.current);
        }
      };
      const queryParams = new URLSearchParams({
        'client-id': sdkSettings.clientId || '',
        'enable-funding': sdkSettings.enableFunding || '',
        currency: sdkSettings.currency || '',
        intent: sdkSettings.intent || ''
      }).toString();
      script.src = `${sdkSettings.sdkBaseUrl}?${queryParams}`;
      document.body.appendChild(script);

      return () => {
        if (script && document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [
    sdkSettings,
    buttonSettings,
    orderAmount,
    handleShippingAddress,
    configureButtonMessages,
    sellerId,
    selectedUsagePattern,
    price,
    selectedFrequency,
    intervalCount,
    totalCycles,
    startDate,
    shipping,
    oneTimeCharges,
    name,
    product,
    appSwitch,
    serverSideShippingCallback,
    buyerEmailAddress
  ]);

  return (
    <>
      <Helmet>
        <style>{`
          .paypal-checkout-sandbox {display:none!important}
        `}</style>
      </Helmet>
      <div className={styles.preview_wrapper} id="guide-preview-wrapper">
        {appIntegrationType === PARTNER && isUserLoggedIn && apps.length > 0 && (
          <div className={styles.seller_cards_container}>
            <SellerCardsWrapper updateSellerId={updateSellerId} />
          </div>
        )}
        <div className={styles.preview_checkout_wrapper}>
          <div className={styles.preview_checkout_container}>
            {buttonLoading && (
              <div className={styles.preview_loader_container}>
                <LoadingSpinner
                  className={styles.preview_loader}
                  screenReaderText="loading"
                  size="md"
                />
              </div>
            )}
            {!appSwitch || !isMobile() ? (
              <PayPalButtons
                style={buttonSettings}
                fundingSource=""
                forceReRender={[
                  buttonSettings,
                  orderAmount,
                  handleShippingAddress,
                  sellerId,
                  appSwitch,
                  selectedUsagePattern,
                  price,
                  selectedFrequency,
                  intervalCount,
                  totalCycles,
                  startDate,
                  shipping,
                  oneTimeCharges,
                  name,
                  product,
                  serverSideShippingCallback
                ]}
                onInit={() => setButtonLoading(false)}
                createVaultSetupToken={subPage === 'recurring' ? createVaultSetupToken : null}
                createOrder={subPage !== 'recurring' ? handleCreateOrder : null}
                onApprove={(data) => onApprove(data)}
                onClick={(data) => trackButtonClick(data.fundingSource)}
                onCancel={stopPolling}
                onError={stopPolling}
                {...(handleShippingAddress
                  ? {
                      onShippingAddressChange: (data) => handleShippingAddressChange(data),
                      onShippingOptionsChange: (data) => handleShippingOptionsChange(data)
                    }
                  : {})}
                {...(configureButtonMessages ? { message: { amount: Number(orderAmount) } } : {})}
              />
            ) : (
              <div ref={paypalButtonContainer} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewWrapper;
